import { useMemo, useRef } from "react";
import { Control, Controller, type FieldValues } from "react-hook-form";
import merge from "lodash/merge";
import omit from "lodash/omit";

import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { InputLabelHeight } from "@covertree/types";
import { getInputLabelHeight } from "@covertree/utils";

import { Tooltip } from "../../content";

export type FormTextFieldProps<TValues extends FieldValues> = Omit<
  TextFieldProps,
  "variant"
> & {
  id: string;
  name: string;
  control: Control<TValues>;
  label: React.ReactNode;
  labelHeight?: InputLabelHeight;
  dataTestId?: string;
  endAdornment?: React.ReactNode;
  type?: React.InputHTMLAttributes<unknown>["type"];
  error?: React.ReactNode;
  tooltip?: string;
  optional?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  fontSize?: number;
  labelFontSize?: number;
  defaultValue?: string | number | null;
  nullOnEmpty?: boolean;
  onValueChange?: (value: unknown) => void;
  valueParser?: (value: string) => unknown;
};

export const FormTextField = <TValues extends FieldValues>({
  id,
  name,
  control,
  label,
  labelHeight = InputLabelHeight.DEFAULT,
  dataTestId,
  tooltip,
  endAdornment,
  type = "text",
  optional,
  disabled = false,
  hiddenLabel,
  readOnly = false,
  fontSize,
  labelFontSize,
  defaultValue = "",
  nullOnEmpty = false,
  onChange,
  onValueChange,
  valueParser,
  ...textFieldProps
}: FormTextFieldProps<TValues>) => {
  const theme = useTheme();
  const fontSizeValue = fontSize ? `${fontSize}px` : undefined;
  const labelFontSizeValue = labelFontSize ? `${labelFontSize}px` : undefined;
  const labelComponentHeight = useMemo(
    () => getInputLabelHeight(theme, labelHeight),
    [labelHeight, theme]
  );
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const labelRef = useRef<HTMLParagraphElement>(null);

  const finalEndAdornment = useMemo(() => {
    if (endAdornment) return endAdornment;
    if (optional)
      return (
        <InputAdornment position="end">
          <Typography variant="subtitle1">Optional</Typography>
        </InputAdornment>
      );
    return undefined;
  }, [endAdornment, optional]);

  return (
    <Controller
      /* eslint-disable  @typescript-eslint/no-explicit-any */
      name={name as any}
      control={control}
      defaultValue={defaultValue as any}
      render={({ field, fieldState: { error } }) => (
        <FormControl variant="standard" fullWidth error={!!error}>
          {!hiddenLabel && (
            <InputLabel
              htmlFor={id}
              id={`${id}-label`}
              sx={{
                position: "static",
                alignItems: "flex-end",
                display: "flex",
                ...(fontSize && {
                  fontSize: labelFontSizeValue ?? fontSizeValue,
                }),
                height: isDesktop ? labelComponentHeight : "auto",
                transform: "none",
                "& > p": {
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  width: "100%",
                  whiteSpace: "break-spaces",
                },
              }}>
              <Typography
                variant="body2"
                ref={labelRef}
                sx={{ margin: 0 }}
                fontSize={labelFontSize ?? fontSize}>
                {label}
                {tooltip && (
                  <Tooltip
                    dataTestId={`${dataTestId}-tooltip`}
                    tooltip={tooltip ?? ""}
                    size="large"
                    fontSize={labelFontSize ?? fontSize}
                    sx={{
                      marginLeft: theme.spacing(),
                      backgroundColor: theme.palette.common.white,
                    }}
                    placement="bottom"
                  />
                )}
              </Typography>
            </InputLabel>
          )}
          <TextField
            id={id}
            type={type}
            error={!!error}
            disabled={disabled}
            variant="outlined"
            data-testid={name}
            sx={{
              ...textFieldProps.sx,
              borderRadius: theme.spacing(1.5),
              ...(fontSize && {
                fontSize: fontSizeValue,
                "& .MuiInputBase-root": {
                  height: fontSize * 3,
                },
                "& .MuiOutlinedInput-root": {
                  minHeight: fontSize * 3,
                },
                "& .MuiInputBase-input": {
                  fontSize: fontSizeValue,
                },
              }),
            }}
            InputProps={merge(
              {
                endAdornment: finalEndAdornment,
                // Keep the same reference: avoids unwanted remounts caused by lodash merge.
                inputComponent: textFieldProps.InputProps?.inputComponent,
                inputProps: {
                  "data-testid": dataTestId,
                  inputMode: textFieldProps.inputMode,
                },
                readOnly: readOnly,
                style: disabled
                  ? {
                      backgroundColor: theme.palette.action.disabledBackground,
                      ...(fontSize && { fontSize: fontSizeValue }),
                    }
                  : { ...(fontSize && { fontSize: fontSizeValue }) },
              },
              textFieldProps.InputProps
            )}
            onChange={(event) => {
              const value = valueParser
                ? valueParser(event.target.value)
                : nullOnEmpty && event.target.value === ""
                  ? null
                  : event.target.value;

              field.onChange(value);
              onValueChange?.(value);
              onChange?.(event);
            }}
            {...omit(field, ["onChange"])}
            {...omit(textFieldProps, ["InputProps", "sx"])}
          />
          <FormHelperText
            error
            {...(!error?.message && {
              sx: { margin: 0, ...(fontSize && { fontSize: fontSizeValue }) },
            })}>
            {error?.message}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};

export default FormTextField;
